import { cn } from '@/lib/utils';
import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import InDecGroupBox from '@/components/ui/buttons/indec-group';
import InputCheck from '@/components/ui/inputs/input-check';
import InputRadio from '@/components/ui/inputs/input-radio';
import CurrencyCodeAdd from '@/components/common/CurrencyCodeAdd';
import { useForm, Controller } from 'react-hook-form';
import { InputSelect } from '@/components/ui/inputs/input-select';
import { InputTextarea } from '@/components/ui/inputs/input-textarea';
import { getFileUrl } from '@/services/CommonService';
import { useSelector, useDispatch } from 'react-redux';
import validationErrors from '@/lib/validationErrors';
import CommonModal from '@/components/ui/modals/CommonModal';
import { addOrUpdateCartItemPOS, removeFromCartPOS } from '@/store/actions';

const ProductInfoModal = ({ modalOpen, setModalOpen, selectProductData = {}, handleProductCauction, setSelectProductData }) => {
    const {
        register,
        setValue,
        getValues,
        formState: { errors },
        handleSubmit,
        control,
        reset,
    } = useForm();
    const { cartItems } = useSelector((state) => state?.GeneralSetting);
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [orderItems, setOrderItems] = useState({});
    const [addToCartItem, setAddToCartItem] = useState({});
    const [sizeVariationValue, setSizeVariationValue] = useState([]);

    useEffect(() => {
        if (selectProductData?._id && selectProductData?._id != '') {
            // Reset form to default values when a new product is selected
            reset({
                item_qty: 1,
                instruction: '',
            });
            
            var itemVariation = { _ids: [], name: [], price: [] };
            var itemVariationTotal = 0;
            var itemExtraTotal = 0;
            var itemTotal = 0;
            if (selectProductData?.item_variation_attributes && selectProductData?.item_variation_attributes.length > 1) {
                var varion_ids = [];
                var varion_name = [];
                var varion_price = [];
                var varion_attribute = [];
                var varion_attribute_name = [];
                selectProductData?.item_variation_attributes.forEach((element) => {
                    var itemVarionDefaultSelect = selectProductData?.item_variations.find((item) => item.item_attribute_id === element._id);
                    if (itemVarionDefaultSelect && element?._id == itemVarionDefaultSelect.item_attribute_id) {
                        varion_ids.push(itemVarionDefaultSelect._id);
                        varion_name.push(itemVarionDefaultSelect.name);
                        varion_price.push(itemVarionDefaultSelect.price);
                        varion_attribute.push(itemVarionDefaultSelect.item_attribute_id);
                        varion_attribute_name.push(element.name);
                        itemVariationTotal += itemVarionDefaultSelect.price;
                        itemTotal += itemVarionDefaultSelect.price;
                    }
                });
                itemVariation = { _ids: varion_ids, name: varion_name, price: varion_price, attribute_ids: varion_attribute, attribute_name: varion_attribute_name };
            } else if (selectProductData?.item_variation_attributes && selectProductData?.item_variation_attributes.length > 0) {
                itemVariation = {
                    _ids: [selectProductData?.item_variations[0]._id],
                    name: [selectProductData?.item_variations[0].name],
                    price: [selectProductData?.item_variations[0].price],
                    attribute_ids: [selectProductData?.item_variations[0].item_attribute_id],
                    attribute_name: [selectProductData?.item_variation_attributes[0].name],
                };
                itemVariationTotal = selectProductData?.item_variations[0].price;
                itemTotal += selectProductData?.item_variations[0].price;
            }

            // Calculate base price (original price after discount, without tax)
            const originalItemPrice = selectProductData?.price - (selectProductData?.price * selectProductData?.offer?.discount_per || 0) / 100;
            const basePriceWithoutTax = originalItemPrice + (itemVariationTotal || 0) + (itemExtraTotal || 0);
            
            // Tax will be calculated on total at the end, not per item
            // Store tax info for reference but set tax_amount to 0
            const taxRate = selectProductData?.item_tax?.tax_rate || 0;
            
            // Total price without tax (tax will be calculated on total subtotal)
            const totalPrice = basePriceWithoutTax;

            setAddToCartItem({
                _id: selectProductData?._id,
                name: selectProductData?.name,
                file_name: selectProductData?.file_name,
                quantity: 1,
                price: basePriceWithoutTax, // Price per unit without tax
                tax_name: selectProductData?.item_tax?.name || '',
                tax_code: selectProductData?.item_tax?.code || '',
                tax_rate: parseFloat(taxRate) || 0, // Always ensure tax_rate is a number
                tax_amount: 0, // Tax calculated on total, not per item
                item_variations: itemVariation,
                item_variation_total: itemVariationTotal,
                item_extras: { _ids: [], name: [], price: [] },
                item_extra_total: 0,
                total_price: totalPrice,
                instruction: '',
            });
        }
    }, [selectProductData?._id, reset]);

    const handleModelClose = () => {
        setModalOpen(!modalOpen);
        document.body.style.overflowY = 'auto';
    };

    const cartItemChange = async (name, value) => {
        var cartItemUpdate = { ...addToCartItem }; // Ensure immutability
        var itemVariationTotal = 0;
        var itemExtraTotal = 0;

        if (name === 'item_qty') {
            cartItemUpdate.quantity = value;
        }

        if (name === 'item_variation') {
            const itemVarionDefaultSelect = selectProductData?.item_variations.find((item) => item._id === value);
            const cartItemInVariosAttributeExits = cartItemUpdate?.item_variations?.attribute_ids.find((item) => item === itemVarionDefaultSelect?.item_attribute_id);

            if (itemVarionDefaultSelect && itemVarionDefaultSelect._id) {
                if (cartItemInVariosAttributeExits) {
                    const indexToRemove = cartItemUpdate?.item_variations?.attribute_ids.indexOf(cartItemInVariosAttributeExits);
                    const cartItemInVariosAttribute = selectProductData?.item_variation_attributes.find((item) => item?._id === itemVarionDefaultSelect?.item_attribute_id);
                    cartItemUpdate?.item_variations?._ids.splice(indexToRemove, 1);
                    cartItemUpdate?.item_variations?.name.splice(indexToRemove, 1);
                    cartItemUpdate?.item_variations?.price.splice(indexToRemove, 1);
                    cartItemUpdate?.item_variations?.attribute_ids.splice(indexToRemove, 1);
                    cartItemUpdate?.item_variations?.attribute_name.splice(indexToRemove, 1);

                    cartItemUpdate?.item_variations?._ids.push(itemVarionDefaultSelect?._id);
                    cartItemUpdate?.item_variations?.name.push(itemVarionDefaultSelect?.name);
                    cartItemUpdate?.item_variations?.price.push(itemVarionDefaultSelect?.price);
                    cartItemUpdate?.item_variations?.attribute_ids.push(itemVarionDefaultSelect?.item_attribute_id);
                    cartItemUpdate?.item_variations?.attribute_name.push(cartItemInVariosAttribute?.name);
                } else {
                    const cartItemInVariosAttribute = selectProductData?.item_variation_attributes.find((item) => item?._id === itemVarionDefaultSelect?.item_attribute_id);
                    cartItemUpdate?.item_variations?._ids.push(itemVarionDefaultSelect?._id);
                    cartItemUpdate?.item_variations?.name.push(itemVarionDefaultSelect?.name);
                    cartItemUpdate?.item_variations?.price.push(itemVarionDefaultSelect?.price);
                    cartItemUpdate?.item_variations?.attribute_ids.push(itemVarionDefaultSelect?.item_attribute_id);
                    cartItemUpdate?.item_variations?.attribute_name.push(cartItemInVariosAttribute?.name);
                }
            }
        }

        if (name === 'item_extra') {
            const itemExtraDefaultSelect = selectProductData?.item_extras.find((item) => item._id === value);
            const cartItemInExtraExits = cartItemUpdate?.item_extras?._ids.find((item) => item === value);

            if (itemExtraDefaultSelect && itemExtraDefaultSelect._id) {
                if (cartItemInExtraExits) {
                    const indexToRemove = cartItemUpdate?.item_extras?._ids.indexOf(cartItemInExtraExits);
                    cartItemUpdate?.item_extras?._ids.splice(indexToRemove, 1);
                    cartItemUpdate?.item_extras?.name.splice(indexToRemove, 1);
                    cartItemUpdate?.item_extras?.price.splice(indexToRemove, 1);
                } else {
                    cartItemUpdate?.item_extras?._ids.push(itemExtraDefaultSelect?._id);
                    cartItemUpdate?.item_extras?.name.push(itemExtraDefaultSelect?.name);
                    cartItemUpdate?.item_extras?.price.push(itemExtraDefaultSelect?.price);
                }
            }
        }

        await Promise.all(
            cartItemUpdate?.item_variations?.price.map(async (element) => {
                itemVariationTotal += element;
            })
        );

        await Promise.all(
            cartItemUpdate?.item_extras?.price.map(async (element) => {
                itemExtraTotal += element;
            })
        );

        // Recalculate base price (original price after discount, without tax)
        const originalItemPrice = selectProductData?.price - (selectProductData?.price * (selectProductData?.offer?.discount_per || 0)) / 100;
        const basePriceWithoutTax = originalItemPrice + (itemVariationTotal || 0) + (itemExtraTotal || 0);
        
        // Tax will be calculated on total at the end, not per item
        const taxRate = selectProductData?.item_tax?.tax_rate || 0;
        
        // Total price without tax (tax will be calculated on total subtotal)
        const totalPrice = basePriceWithoutTax * cartItemUpdate?.quantity;
        
        // Update totals
        cartItemUpdate.item_variation_total = itemVariationTotal;
        cartItemUpdate.item_extra_total = itemExtraTotal;
        cartItemUpdate.price = basePriceWithoutTax; // Price per unit without tax
        cartItemUpdate.tax_amount = 0; // Tax calculated on total, not per item
        cartItemUpdate.tax_rate = parseFloat(taxRate) || 0; // Always ensure tax_rate is a number
        cartItemUpdate.tax_name = selectProductData?.item_tax?.name || '';
        cartItemUpdate.tax_code = selectProductData?.item_tax?.code || '';
        cartItemUpdate.total_price = totalPrice;

        setAddToCartItem(cartItemUpdate);
    };

    const onSubmit = (data) => {
        var cartItemAdd = { ...addToCartItem }; // Ensure immutability
        cartItemAdd.instruction = data.instruction;
        dispatch(addOrUpdateCartItemPOS(cartItemAdd));
        
        // Reset form and state after adding to cart
        reset({
            item_qty: 1,
            instruction: '',
        });
        setAddToCartItem({});
        
        handleModelClose();
        setSelectProductData('');
    };

    return (
        <CommonModal modelTitle={'Beef with Mix Vegetables'} addonsclassName={'ff-modal'} modelHeaderHide={true} openModel={modalOpen} handleModelClose={handleModelClose}>
            <div className="modal-dialog max-w-[647px]">
                <div className="modal-header items-start border-none pb-0">
                    <div className="flex gap-4">
                        <img className="flex-shrink-0 w-[72px] h-[72px] object-cover rounded-lg" src={getFileUrl(selectProductData?.file_name, 'product')} alt="thumbnail" />
                        <div className="flex-auto">
                            <div className="flex items-start gap-2 mb-1">
                                <h3 className="text-sm font-semibold capitalize">{selectProductData?.name}</h3>
                                <button type="button" className="info-btn mt-0.5" onClick={() => handleProductCauction(selectProductData)}>
                                    <i className="lab lab-information font-fill-paragraph transition lab-font-size-16"></i>
                                </button>
                            </div>
                            <p className="text-xs mb-2">{selectProductData?.description}</p>
                            <h4 className="text-sm font-semibold">
                                <CurrencyCodeAdd price={selectProductData?.price - (selectProductData?.price * selectProductData?.offer?.discount_per || 0) / 100} />
                            </h4>
                        </div>
                    </div>
                    <button className="modal-close lab-close-circle-line font-fill-danger lab-font-size-24" onClick={() => handleModelClose()}></button>
                </div>

                <div className="modal-body">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="flex items-center gap-2 mb-4">
                            <h3 className="text-sm leading-6 font-medium first-letter:uppercase text-heading">Quantity:</h3>
                            <InDecGroupBox 
                                inputName="item_qty" 
                                inputValue={addToCartItem?.quantity || 1}
                                onChangeValue={cartItemChange} 
                                groupClassName={'py-1 px-2 rounded-xl bg-[#F7F7FC]'} 
                                buttonClassName={'w-[18px] h-[18px] text-[10px]'} 
                            />
                        </div>

                        <div className="mb-4">
                            {selectProductData?.item_variation_attributes && selectProductData?.item_variation_attributes.length > 1 ? (
                                <div className="mb-4">
                                    <div className="row relative z-[2]">
                                        {selectProductData?.item_variation_attributes?.map((variationAttribute, index) => (
                                            <div className="col-12 sm:col-6" key={index}>
                                                <Controller
                                                    control={control}
                                                    name={variationAttribute?.name}
                                                    render={({ field }) => (
                                                        <InputSelect
                                                            {...field}
                                                            id={variationAttribute?.name}
                                                            size="sm"
                                                            rules={{ required: true }}
                                                            labelText={variationAttribute?.name}
                                                            name={variationAttribute?.name}
                                                            labelStyleClassName="text-sm leading-6 block font-medium capitalize mb-1.5 text-heading"
                                                            options={selectProductData?.item_variations
                                                                ?.filter((variation) => {
                                                                    return variation?.item_attribute_id == variationAttribute?._id; // Apply filter otherwise
                                                                })
                                                                .map((variation, index) => ({
                                                                    value: variation._id,
                                                                    label: `${variation.name} +$${variation.price}`,
                                                                }))}
                                                            value={
                                                                selectProductData?.item_variations
                                                                    ?.filter((variation) => variation?.item_attribute_id === variationAttribute?._id)
                                                                    .map((variation) => ({
                                                                        value: variation._id,
                                                                        label: `${variation.name} +$${variation.price}`,
                                                                    }))
                                                                    ?.find((option) => addToCartItem?.item_variations?._ids?.some((id) => id === option.value)) || null
                                                            }
                                                            onChange={(e) => cartItemChange('item_variation', e)}
                                                            errorType={errors?.[variationAttribute?.name]?.type}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : selectProductData?.item_variation_attributes && selectProductData?.item_variation_attributes.length > 0 ? (
                                <>
                                    <h3 className="text-sm leading-6 font-medium capitalize mb-2 text-heading">{selectProductData?.item_variation_attributes[0]?.name}</h3>
                                    <div className="swiper size-swiper">
                                        <div className="size-tabs">
                                            <Swiper speed={1000} slidesPerView="auto" spaceBetween={16}>
                                                {selectProductData?.item_variations?.map((variation, index) => (
                                                    <SwiperSlide className="!w-fit">
                                                        <InputRadio
                                                            key={index}
                                                            groupClassName={cn(
                                                                'variation-margin-right w-full h-[52px] cursor-pointer py-2 px-3 gap-2 rounded-lg flex items-center border transition border-[#F7F7FC] bg-[#F7F7FC]',
                                                                addToCartItem?.item_variations?._ids?.some((id) => id === variation._id) && 'active'
                                                            )}
                                                            inputClassName="sm"
                                                            labelClassName={'!ps-0'}
                                                            name="sizeVariation"
                                                            checked={addToCartItem?.item_variations?._ids?.some((id) => id === variation._id) ? true : false}
                                                            onClick={() => cartItemChange('item_variation', variation._id)}
                                                            labelText={
                                                                <>
                                                                    <span className="block capitalize text-xs text-heading">{variation?.name}</span>
                                                                    {variation?.price && (
                                                                        <span className="block text-xs font-medium text-heading">
                                                                            <CurrencyCodeAdd price={variation?.price} />
                                                                        </span>
                                                                    )}
                                                                </>
                                                            }
                                                        />
                                                    </SwiperSlide>
                                                ))}
                                            </Swiper>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <></>
                            )}
                        </div>

                        {selectProductData?.item_extras && selectProductData?.item_extras.length > 0 ? (
                            <div className="mb-4">
                                <h3 className="text-sm leading-6 font-medium capitalize mb-2 text-heading">Extras</h3>
                                <div className="extra-swiper">
                                    <Swiper speed={1000} slidesPerView="auto" spaceBetween={16}>
                                        {selectProductData?.item_extras?.map((extra, index) => (
                                            <SwiperSlide className="!w-fit !relative">
                                                <InputCheck
                                                    key={index}
                                                    name={extra.name}
                                                    groupClassName={cn(
                                                        'extra w-full h-[52px] cursor-pointer py-2 px-3 gap-3 rounded-lg flex items-center border transition border-[#F7F7FC] bg-[#F7F7FC]',
                                                        addToCartItem?.item_extras?._ids.find((item) => item === extra._id) && 'active'
                                                    )}
                                                    inputClassName={'w-3 h-3'}
                                                    labelClassName="!ps-0"
                                                    onClick={() => cartItemChange('item_extra', extra._id)}
                                                    checked={addToCartItem?.item_extras?._ids.find((item) => item === extra._id) ? true : false}
                                                    labelText={
                                                        <>
                                                            <span className="block capitalize mb-1 text-xs text-heading">{extra.name}</span>
                                                            {extra?.price && (
                                                                <span className="block text-xs font-medium text-heading">
                                                                    <CurrencyCodeAdd price={extra?.price} />
                                                                </span>
                                                            )}
                                                        </>
                                                    }
                                                />
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>
                            </div>
                        ) : (
                            <></>
                        )}

                        {selectProductData?.item_addon_details && selectProductData?.item_addon_details.length > 0 ? (
                            // <div className="mb-5">
                            //     <h3 className="text-sm leading-6 font-medium capitalize mb-2 text-heading">Addons</h3>
                            //     <div className="swiper addon-swiper">
                            //         <Swiper speed={1000} slidesPerView="auto" spaceBetween={16}>
                            //             {selectProductData?.item_addon_details.map((addon, index) => (
                            //                 <SwiperSlide>
                            //                     <div className="!w-fit !relative">
                            //                         <div className="addon cursor-pointer w-fit min-w-[200px] h-[70px] rounded-lg flex border border-[#EFF0F6]">
                            //                             <img
                            //                                 className="w-[68px] h-full object-cover ltr:rounded-l-lg rtl:rounded-r-lg flex-shrink-0"
                            //                                 src={getFileUrl(addon?.file_name, 'product')}
                            //                                 alt="thumbnail"
                            //                             />
                            //                             <div className="ltr:rounded-r-lg rtl:rounded-l-lg w-full py-1 px-2">
                            //                                 <span className="block text-xs text-ellipsis whitespace-nowrap overflow-hidden w-fit max-w-[100px] capitalize text-heading">
                            //                                     {addon?.name}
                            //                                 </span>
                            //                                 <p className="text-left text-[10px] leading-4 capitalize mb-1.5 cursor-pointer"></p>
                            //                                 <span className="block text-xs font-semibold text-heading ltr:text-left rtl:text-right">
                            //                                     <CurrencyCodeAdd price={addon?.price} />
                            //                                 </span>
                            //                             </div>
                            //                         </div>
                            //                         <div className="flex flex-col items-end justify-between h-full absolute top-0 ltr:right-0 rtl:left-0 z-10 p-2">
                            //                             <button type="button" className="info-btn" data-modal="#item-info-modal" onClick={() => handleProductCauction(addon?._id)}>
                            //                                 <i className="lab lab-information font-fill-paragraph transition lab-font-size-16"></i>
                            //                             </button>
                            //                             <InDecGroupBox />
                            //                         </div>
                            //                     </div>
                            //                 </SwiperSlide>
                            //             ))}
                            //         </Swiper>
                            //     </div>
                            // </div>
                            <></>
                        ) : (
                            <></>
                        )}
                        <div className="mb-6">
                            <Controller
                                control={control}
                                name={'instruction'}
                                render={({ field }) => (
                                    <InputTextarea
                                        {...field}
                                        labelStyleClassName="text-xs leading-6 font-medium capitalize mb-2 text-heading"
                                        labelText="Special Instructions"
                                        placeholder="Add Note"
                                        className="rounded-lg border py-1.5 px-2 placeholder:text-[10px] placeholder:text-[#6E7191]"></InputTextarea>
                                )}
                            />
                        </div>
                        <button type="submit" className="flex items-center justify-center gap-3 rounded-3xl text-base py-3 px-3 font-medium w-full text-white bg-primary">
                            <i className="lab-bag-2"></i>
                            <span>
                                Add to Cart - <CurrencyCodeAdd price={addToCartItem?.total_price} />
                            </span>
                        </button>
                    </form>
                </div>
            </div>
        </CommonModal>
    );
};

export default ProductInfoModal;
